import { useParams } from "react-router-dom";
import { createTranslator } from "next-intl";
import content from "../../content/claims.json";
import { MainLayout } from "../../layouts/MainLayout";
import { Loader } from "../../shared/Loader";
import { Card } from "../../shared/Card";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import { getStatusViewModel } from "./getStatusViewModel";
import { useClaim } from "./hooks";
import { StatusHero } from "./components/StatusHero";
import { ClaimStepper } from "./components/ClaimStepper";
import { ResolutionPanel } from "./components/ResolutionPanel";
import { ClaimItemsList } from "./components/ClaimItemsList";
import { OrderAndClaimDetails } from "./components/OrderAndClaimDetails";
import { ShippingAddressCard } from "./components/ShippingAddressCard";
import { PhotosSection } from "./components/PhotosSection";
import { CoverageSummary } from "./components/CoverageSummary";

export const Status = () => {
  const { isThemeLoading, isAppLoading, locale } = useTenantTheme();
  const { claimNumber } = useParams();
  const { claim } = useClaim(claimNumber);

  const t = createTranslator({
    locale,
    messages: content[locale as keyof typeof content],
  });

  if (isThemeLoading || isAppLoading) {
    return <Loader />;
  }

  if (!claim) {
    return (
      <MainLayout>
        <Card title={t("status.notFound")} compact />
      </MainLayout>
    );
  }

  const viewModel = getStatusViewModel(claim, t);

  return (
    <MainLayout helpText={t("helpText")}>
      <Card compact>
        <StatusHero
          hero={viewModel.hero}
          claimNumber={claim.claim_number}
          nextSteps={viewModel.nextSteps}
          notificationEmail={viewModel.notificationEmail}
          statusAnnouncement={viewModel.statusAnnouncement}
          t={t}
        />
        <ClaimStepper steps={viewModel.stepperSteps} t={t} />
        <ResolutionPanel resolution={viewModel.resolution} t={t} />
        {viewModel.showShippingAddress ? (
          <ShippingAddressCard address={claim.shipping_address} t={t} />
        ) : null}
        <OrderAndClaimDetails claim={claim} t={t} />
        {viewModel.showClaimItems ? (
          <ClaimItemsList items={claim.claim_items} claim={claim} t={t} />
        ) : null}
        <PhotosSection imageUrls={claim.image_urls} t={t} />
        <CoverageSummary
          claim={claim}
          isAwaitingReview={viewModel.isAwaitingReview}
          t={t}
        />
      </Card>
    </MainLayout>
  );
};
